import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { cfSrcForImage } from '~/components/CategoryBlocks/utils';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import type { Category } from '~/stores/CategoriesStore';
import { CategoryBlockTitle } from './CategoryBlockTitle';
import { createCategoryUrl } from './utils';

interface CategoryBlockHeaderProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  category: Category;
  showViewAll?: boolean;
  isCategoriesBlock?: boolean;
}

export const CategoryBlockHeader: React.FC<CategoryBlockHeaderProps> = ({
  category,
  showViewAll = true,
  isCategoriesBlock = false,
  className,
  ...props
}) => {
  const { isMobile } = useGlobal();
  const { t } = useTranslation();
  const { handleClickCategory } = useCategoryAnalytics();
  const { pathname } = useLocation();

  const categoryId = isCategoriesBlock
    ? category.id
    : category.subcategory[0]?.id;
  const linkTo =
    pathname !== '/search' && categoryId
      ? createCategoryUrl({ id: categoryId })
      : undefined;

  const ViewAllComponent = isMobile
    ? Typography.Text100Medium
    : Typography.Text200Medium;

  return (
    <div
      {...props}
      className={cn(
        'relative flex items-center',
        showViewAll ? 'justify-between' : 'justify-center',
        className,
      )}
    >
      {isMobile && category.previewImage && (
        <div
          style={{
            backgroundImage: `url(${cfSrcForImage(category.previewImage, { width: 320 })})`,
          }}
          className="absolute -top-[37px] -z-1 h-[140px] w-[154px] bg-center bg-contain bg-no-repeat"
        />
      )}
      <CategoryBlockTitle>{category.name}</CategoryBlockTitle>
      {showViewAll && linkTo && (
        <Link
          to={linkTo}
          aria-label={t('viewAll')}
          className="outline! cursor-pointer rounded-full px-3 py-[5px] text-text-secondary no-underline outline-stroke-primary! transition-colors duration-300 hover:bg-surface-transparent-2 hover:text-text-primary hover:no-underline md:px-4 md:py-3"
          onClick={categoryId ? handleClickCategory(categoryId) : undefined}
        >
          <ViewAllComponent as="span" className="block">
            {t('viewAll')}
          </ViewAllComponent>
        </Link>
      )}
    </div>
  );
};
